/**
 * MessagesView Component
 * Email-style two-column inbox for guest messages.
 *   Left panel  – scrollable message list (sender, subject, preview, unread dot)
 *   Right panel – full message detail rendered when a row is selected
 *
 * Unread tracking is held in local state — selecting a message marks it read.
 * Filename kept as MessagesPlaceholderView so ConciergeGrid imports remain unchanged.
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { mockMessages } from '@/data/mockHotelData';
import { getReadIds, markRead } from '@/services/messageReadStore';
import { API_BASE } from '@/config/api';
import type { Message } from '@/types';

// =============================================================================
// LEDGER MESSAGE FETCHING
// Polls activity ledger for type=MESSAGE records (sent by staff)
// and merges them with static mock messages.
// =============================================================================

async function fetchLedgerMessages(): Promise<Message[]> {
  try {
    const res = await fetch(`${API_BASE}/activity-ledger?type=MESSAGE&limit=50`);
    if (!res.ok) return [];
    const data = await res.json();
    return (data.activities ?? []).map((r: any): Message => ({
      id: r.id,
      from: r.metadata?.staffMember ?? 'Hotel Staff',
      title: r.metadata?.subject ?? 'Message from Hotel',
      content: r.metadata?.message ?? '',
      timestamp: typeof r.timestamp === 'string' ? r.timestamp : new Date(r.timestamp).toISOString(),
      read: false,
      type: 'info',
    }));
  } catch {
    return [];
  }
}

// =============================================================================
// CONFIG
// =============================================================================

const TYPE_CONFIG: Record<string, { icon: string; color: string }> = {
  welcome: { icon: '🏨', color: 'text-teal-400' },
  info:    { icon: '📌', color: 'text-blue-400' },
  alert:   { icon: '🔔', color: 'text-amber-400' },
};

// =============================================================================
// UTILITIES
// =============================================================================

function formatListDate(timestamp: string): string {
  const d = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString('en-US', { weekday: 'short' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDetailDate(timestamp: string): string {
  return new Date(timestamp).toLocaleString('en-US', {
    weekday: 'long',
    month:   'long',
    day:     'numeric',
    hour:    '2-digit',
    minute:  '2-digit',
  });
}

// =============================================================================
// COMPONENT
// =============================================================================

interface MessagesViewProps {
  isActive: boolean;
  onClose: () => void;
  /** P2.9-1: Register a synchronous voice command handler so GlobalVoiceOverlay can call it and speak the response itself */
  onMessagesVoiceCommandReady?: (handler: (transcript: string) => string) => void;
}

export function MessagesPlaceholderView({ isActive, onClose, onMessagesVoiceCommandReady }: MessagesViewProps) {
  // All messages = mock (static) + real staff messages from ledger
  const [messages, setMessages] = useState<Message[]>(mockMessages);

  // Initialise from localStorage; also seed any mock messages flagged read:true
  const [readIds, setReadIds] = useState<Set<string>>(() => {
    const persisted = getReadIds();
    // Pre-mark mocks that ship as read so they never appear as new
    mockMessages.filter(m => m.read).forEach(m => persisted.add(m.id));
    return persisted;
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const isActiveRef = useRef(isActive);
  const listPanelRef = useRef<HTMLDivElement>(null);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  // Poll ledger for staff-sent messages and merge with mocks
  useEffect(() => {
    async function refresh() {
      const ledger = await fetchLedgerMessages();
      if (ledger.length === 0) return;
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const newOnes = ledger.filter(m => !existingIds.has(m.id));
        if (newOnes.length === 0) return prev;
        // Notify sidebar badge so it recounts immediately
        window.dispatchEvent(new CustomEvent('zaxia:ledger-messages-updated', {
          detail: { ids: ledger.map(m => m.id) },
        }));
        // Merge and sort by timestamp descending
        return [...prev, ...newOnes].sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      });
    }
    refresh();
    const interval = setInterval(refresh, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Scroll selected item fully into view
  useEffect(() => {
    if (!selectedId || !listPanelRef.current) return;
    const btn = listPanelRef.current.querySelector<HTMLButtonElement>(`[data-msg-id="${selectedId}"]`);
    btn?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId]);

  // Mark selected as read — persist to localStorage so it survives re-mounts
  useEffect(() => {
    if (selectedId && !readIds.has(selectedId)) {
      markRead(selectedId);
      setReadIds(prev => new Set([...prev, selectedId]));
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Synchronous voice command processor — captured in a ref so it always has
  // fresh access to messages/selectedId/onClose without re-registering.
  const voiceCmdRef = useRef<(transcript: string) => string>(() => "Say 'next', 'previous', 'read', or a message number.");

  useEffect(() => {
    // Ordinal / word-number → 1-based index
    const ORDINALS: Record<string, number> = {
      first: 1, '1st': 1, second: 2, '2nd': 2, third: 3, '3rd': 3,
      fourth: 4, '4th': 4, fifth: 5, '5th': 5, sixth: 6, '6th': 6,
      seventh: 7, '7th': 7, eighth: 8, '8th': 8, ninth: 9, '9th': 9, tenth: 10, '10th': 10,
      one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
    };
    voiceCmdRef.current = (transcript: string): string => {
      const lower = transcript.toLowerCase().trim();
      const idx = messages.findIndex(m => m.id === selectedId);

      if (/\b(next|down)\b/.test(lower)) {
        if (idx < messages.length - 1) { setSelectedId(messages[idx + 1]!.id); return 'Next message.'; }
        return 'No more messages.';
      }
      if (/\b(prev(ious)?|back|up)\b/.test(lower)) {
        if (idx > 0) { setSelectedId(messages[idx - 1]!.id); return 'Previous message.'; }
        return 'Already at the first message.';
      }
      if (/\bread\b/.test(lower)) {
        const msg = messages.find(m => m.id === selectedId);
        return msg ? `${msg.title}. From ${msg.from}. ${msg.content}` : 'Please select a message first.';
      }
      if (/\b(close|go\s*back|exit|leave)\b/.test(lower)) {
        onClose();
        return 'Closing messages.';
      }
      // Digit or ordinal/word number
      const numMatch =
        lower.match(/\b(?:open|select|show|go\s+to|message)\s+(?:the\s+)?(\d+)/) ||
        lower.match(/\b(?:open|select|show|go\s+to|message)\s+(?:the\s+)?(\w+)/) ||
        lower.match(/\b(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|one|two|three|four|five|six|seven|eight|nine|ten|\d+(?:st|nd|rd|th)?)\b/);
      const rawToken = numMatch ? numMatch[1]! : null;
      const n = rawToken ? (parseInt(rawToken, 10) || ORDINALS[rawToken] || 0) : 0;
      if (n > 0) {
        const msgIdx = n - 1;
        if (msgIdx >= 0 && msgIdx < messages.length) { setSelectedId(messages[msgIdx]!.id); return `Opening message ${n}.`; }
        return `I only have ${messages.length} messages.`;
      }
      return "Say 'next', 'previous', 'read', or a message number.";
    };
  }, [messages, selectedId, onClose]);

  // Register handler once (ref wrapper means re-registration is unnecessary)
  useEffect(() => {
    onMessagesVoiceCommandReady?.((transcript) => voiceCmdRef.current(transcript));
  }, [onMessagesVoiceCommandReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActiveRef.current) return;
      if (e.defaultPrevented) return;
      const idx = messages.findIndex(m => m.id === selectedId);
      switch (e.key) {
        case 'Escape':
          e.preventDefault(); e.stopImmediatePropagation(); onClose(); break;
        case 'ArrowUp':
          e.preventDefault(); e.stopImmediatePropagation();
          if (idx > 0) setSelectedId(messages[idx - 1]!.id);
          break;
        case 'ArrowDown':
          e.preventDefault(); e.stopImmediatePropagation();
          if (idx < messages.length - 1) setSelectedId(messages[idx + 1]!.id);
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose, selectedId, messages]);

  const selectedMessage = messages.find(m => m.id === selectedId) ?? null;
  const unreadCount = messages.filter(m => !readIds.has(m.id)).length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="h-full w-full bg-zinc-950 flex flex-col outline-none overflow-hidden rounded-xl"
    >
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex-shrink-0 px-5 py-3 bg-black/40 border-b border-white/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-white font-semibold text-sm tracking-wide">Inbox</span>
          {unreadCount > 0 && (
            <span className="bg-teal-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full leading-none">
              {unreadCount} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-white/30 hover:text-white/60 text-xs transition-colors"
          >
            ✕ Close
          </button>
        </div>
      </div>

      {/* ── Body: list (left) + detail (right) ─────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── LEFT: Message List ─────────────────────────────── */}
        <div ref={listPanelRef} className="messages-list-panel w-[38%] flex-shrink-0 border-r border-white/[0.06] overflow-y-auto">
          {messages.map(msg => {
            const isUnread   = !readIds.has(msg.id);
            const isSelected = msg.id === selectedId;
            const cfg        = TYPE_CONFIG[msg.type] ?? TYPE_CONFIG['info']!;

            return (
              <button
                key={msg.id}
                data-msg-id={msg.id}
                onClick={() => setSelectedId(msg.id)}
                className={[
                  'messages-list-item w-full text-left px-4 py-3.5 border-b border-white/[0.04] transition-colors',
                  'relative focus:outline-none',
                  isSelected
                    ? 'bg-teal-600/20 border-l-2 border-l-teal-500'
                    : isUnread
                    ? 'bg-white/[0.07] hover:bg-white/10 border-l-2 border-l-transparent'
                    : 'bg-transparent hover:bg-white/[0.04] border-l-2 border-l-transparent',
                ].join(' ')}
              >
                <div className="flex items-start gap-2.5">
                  {/* Type icon */}
                  <span className="text-sm flex-shrink-0 mt-0.5">{cfg.icon}</span>

                  <div className="flex-1 min-w-0">
                    {/* From + timestamp */}
                    <div className="flex items-center justify-between mb-0.5 gap-1">
                      <span className={`messages-sender text-xs truncate ${isUnread ? 'font-semibold text-white' : 'text-white/50'}`}>
                        {msg.from ?? 'Hotel'}
                      </span>
                      <span className="text-[10px] text-white/25 flex-shrink-0">
                        {formatListDate(msg.timestamp)}
                      </span>
                    </div>

                    {/* Subject */}
                    <p className={`text-[11px] truncate ${isUnread ? 'text-white/90 font-medium' : 'text-white/40'}`}>
                      {msg.title}
                    </p>

                    {/* Preview snippet */}
                    <p className="messages-preview-snippet text-[10px] text-white/25 truncate mt-0.5 leading-tight">
                      {msg.content}
                    </p>
                  </div>

                  {/* Unread indicator dot */}
                  {isUnread && (
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0 mt-1.5" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* ── RIGHT: Message Detail ───────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {selectedMessage ? (
              <motion.div
                key={selectedMessage.id}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="px-7 py-5"
              >
                {/* Subject */}
                <div className="mb-5 pb-4 border-b border-white/[0.07]">
                  <h2 className="text-white font-semibold text-[15px] leading-snug mb-3">
                    {selectedMessage.title}
                  </h2>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
                    <span className="flex items-center gap-1.5">
                      <span>{TYPE_CONFIG[selectedMessage.type]?.icon}</span>
                      <span className="text-white/60 font-medium">{selectedMessage.from ?? 'Hotel'}</span>
                    </span>
                    <span className="text-white/20">·</span>
                    <span className="text-white/30">{formatDetailDate(selectedMessage.timestamp)}</span>
                  </div>
                </div>

                {/* Body */}
                <p className="messages-body text-white/75 text-sm leading-relaxed whitespace-pre-wrap">
                  {selectedMessage.content}
                </p>

                {/* Type badge */}
                <div className="mt-8">
                  <span className={[
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border',
                    selectedMessage.type === 'alert'
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                      : selectedMessage.type === 'welcome'
                      ? 'bg-teal-500/10 border-teal-500/30 text-teal-400'
                      : 'bg-blue-500/10 border-blue-500/30 text-blue-400',
                  ].join(' ')}>
                    {TYPE_CONFIG[selectedMessage.type]?.icon}{' '}
                    {selectedMessage.type.charAt(0).toUpperCase() + selectedMessage.type.slice(1)}
                  </span>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-center h-full text-white/25 text-sm"
              >
                Select a message to read
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

export default MessagesPlaceholderView;
